import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Users, DollarSign, TrendingUp, Link2, Eye, Edit } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Affiliate {
  user_id: string;
  referral_code: string;
  total_referrals: number;
  total_earnings: number;
  pending_earnings: number;
  username?: string;
  email?: string;
}

interface Transaction {
  id: string;
  referrer_id: string;
  referred_user_id: string;
  status: string;
  purchase_amount: number;
  commission_earned: number;
  created_at: string;
  referrer_username?: string;
  referred_username?: string;
}

interface TrackingLink {
  id: string;
  user_id: string;
  link_name: string;
  utm_campaign: string;
  clicks: number;
  conversions: number;
  revenue_generated: number;
  username?: string;
}

interface PayoutRequest {
  id: string;
  user_id: string;
  amount: number;
  status: string;
  payment_method: string;
  requested_at: string;
  username?: string;
}

const AdminAffiliates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [trackingLinks, setTrackingLinks] = useState<TrackingLink[]>([]);
  const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(null);
  const [editingCode, setEditingCode] = useState("");

  useEffect(() => {
    checkAdminAndLoadData();
  }, []);

  const logAdminAction = async (action: string, metadata: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from("analytics_events").insert({
        user_id: user?.id,
        event_type: "admin_action",
        metadata: {
          action,
          ...metadata,
          timestamp: new Date().toISOString(),
        },
      });
    } catch (error) {
      console.error("Failed to log admin action:", error);
    }
  };

  const checkAdminAndLoadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      // Check if user is admin
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .single();

      if (!roles) {
        toast.error("Access denied. Admin only.");
        navigate("/dashboard");
        return;
      }

      await logAdminAction("view_affiliate_dashboard", {});
      await loadAllData();
    } catch (error: any) {
      console.error(error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    // Load all affiliates with their stats
    const { data: codes } = await supabase
      .from("referral_codes")
      .select("user_id, code");

    if (!codes) return;

    const { data: stats } = await supabase
      .from("referral_stats")
      .select("*");

    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, username");

    const profileMap = new Map(profiles?.map(p => [p.id, p.username]) || []);
    const statsMap = new Map(stats?.map(s => [s.user_id, s]) || []);

    const affiliatesData = codes.map(code => {
      const userStats = statsMap.get(code.user_id) || {
        total_referrals: 0,
        total_earnings: 0,
        pending_earnings: 0,
      };
      return {
        user_id: code.user_id,
        referral_code: code.code,
        username: profileMap.get(code.user_id) || "Unknown",
        ...userStats,
      };
    });

    setAffiliates(affiliatesData);

    // Load all transactions
    const { data: referrals } = await supabase
      .from("referrals")
      .select("*")
      .order("created_at", { ascending: false });

    if (referrals) {
      const enriched = referrals.map(r => ({
        ...r,
        referrer_username: profileMap.get(r.referrer_id),
        referred_username: profileMap.get(r.referred_user_id),
      }));
      setTransactions(enriched as any);
    }

    // Load all tracking links
    const { data: links } = await supabase
      .from("tracking_links")
      .select("*")
      .eq("is_active", true)
      .order("created_at", { ascending: false });

    if (links) {
      const enrichedLinks = links.map(l => ({
        ...l,
        username: profileMap.get(l.user_id),
      }));
      setTrackingLinks(enrichedLinks as any);
    }

    // Load all payout requests
    const { data: payouts } = await supabase
      .from("payouts")
      .select("*")
      .order("requested_at", { ascending: false });

    if (payouts) {
      const enrichedPayouts = payouts.map(p => ({
        ...p,
        username: profileMap.get(p.user_id),
      }));
      setPayoutRequests(enrichedPayouts as any);
    }
  };

  const updateReferralCode = async () => {
    if (!selectedAffiliate || !editingCode) return;

    try {
      await supabase
        .from("referral_codes")
        .update({ code: editingCode })
        .eq("user_id", selectedAffiliate.user_id);

      await logAdminAction("update_referral_code", {
        user_id: selectedAffiliate.user_id,
        old_code: selectedAffiliate.referral_code,
        new_code: editingCode,
      });

      toast.success("Referral code updated");
      setSelectedAffiliate(null);
      loadAllData();
    } catch (error) {
      toast.error("Failed to update code");
      console.error(error);
    }
  };

  const updatePayoutStatus = async (payoutId: string, status: string) => {
    try {
      const updateData: any = { status };
      if (status === "approved") updateData.approved_at = new Date().toISOString();
      if (status === "paid") updateData.paid_at = new Date().toISOString();

      await supabase
        .from("payouts")
        .update(updateData)
        .eq("id", payoutId);

      await logAdminAction("update_payout_status", {
        payout_id: payoutId,
        new_status: status,
      });

      toast.success("Payout status updated");
      loadAllData();
    } catch (error) {
      toast.error("Failed to update payout");
      console.error(error);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      pending: "secondary",
      active: "default",
      completed: "default",
      approved: "default",
      paid: "default",
      rejected: "destructive",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalAffiliates = affiliates.length;
  const totalEarnings = affiliates.reduce((sum, a) => sum + (a.total_earnings || 0), 0);
  const pendingPayouts = payoutRequests.filter(p => p.status === "pending").length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate("/admin")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-4xl font-bold">Affiliate Management</h1>
              <p className="text-muted-foreground mt-1">
                Monitor and manage all affiliate activities
              </p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Affiliates</CardTitle>
              <Users className="w-4 h-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{totalAffiliates}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Earnings Paid</CardTitle>
              <DollarSign className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                ${(totalEarnings / 100).toFixed(2)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pending Payouts</CardTitle>
              <TrendingUp className="w-4 h-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{pendingPayouts}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="affiliates" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="affiliates">Affiliates</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="tracking">Tracking Links</TabsTrigger>
            <TabsTrigger value="payouts">Payouts</TabsTrigger>
          </TabsList>

          {/* Affiliates Tab */}
          <TabsContent value="affiliates">
            <Card>
              <CardHeader>
                <CardTitle>All Affiliates</CardTitle>
                <CardDescription>View and manage affiliate accounts</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Username</TableHead>
                      <TableHead>Referral Code</TableHead>
                      <TableHead>Total Referrals</TableHead>
                      <TableHead>Total Earnings</TableHead>
                      <TableHead>Pending</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {affiliates.map((affiliate) => (
                      <TableRow key={affiliate.user_id}>
                        <TableCell className="font-medium">{affiliate.username}</TableCell>
                        <TableCell className="font-mono">{affiliate.referral_code}</TableCell>
                        <TableCell>{affiliate.total_referrals || 0}</TableCell>
                        <TableCell className="text-green-600">
                          ${((affiliate.total_earnings || 0) / 100).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-blue-600">
                          ${((affiliate.pending_earnings || 0) / 100).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedAffiliate(affiliate);
                                  setEditingCode(affiliate.referral_code);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Edit Referral Code</DialogTitle>
                                <DialogDescription>
                                  Update referral code for {affiliate.username}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label htmlFor="code">Referral Code</Label>
                                  <Input
                                    id="code"
                                    value={editingCode}
                                    onChange={(e) => setEditingCode(e.target.value.toUpperCase())}
                                    placeholder="REFCODE123"
                                  />
                                </div>
                              </div>
                              <DialogFooter>
                                <Button onClick={updateReferralCode}>Update Code</Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>All Transactions</CardTitle>
                <CardDescription>View all affiliate referrals and commissions</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Referrer</TableHead>
                      <TableHead>Referred User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Purchase Amount</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{transaction.referrer_username || "Unknown"}</TableCell>
                        <TableCell>{transaction.referred_username || "Unknown"}</TableCell>
                        <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                        <TableCell>
                          ${(transaction.purchase_amount / 100).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-green-600 font-semibold">
                          ${(transaction.commission_earned / 100).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {new Date(transaction.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tracking Links Tab */}
          <TabsContent value="tracking">
            <Card>
              <CardHeader>
                <CardTitle>All Tracking Links</CardTitle>
                <CardDescription>Monitor custom tracking link performance</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Affiliate</TableHead>
                      <TableHead>Link Name</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Clicks</TableHead>
                      <TableHead>Conversions</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trackingLinks.map((link) => (
                      <TableRow key={link.id}>
                        <TableCell>{link.username || "Unknown"}</TableCell>
                        <TableCell className="font-medium">{link.link_name}</TableCell>
                        <TableCell>{link.utm_campaign}</TableCell>
                        <TableCell>{link.clicks}</TableCell>
                        <TableCell>{link.conversions}</TableCell>
                        <TableCell>
                          {link.clicks > 0
                            ? ((link.conversions / link.clicks) * 100).toFixed(1)
                            : 0}%
                        </TableCell>
                        <TableCell className="text-green-600">
                          ${(link.revenue_generated / 100).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <Card>
              <CardHeader>
                <CardTitle>Payout Requests</CardTitle>
                <CardDescription>Review and approve payout requests</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Affiliate</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payoutRequests.map((payout) => (
                      <TableRow key={payout.id}>
                        <TableCell>{payout.username || "Unknown"}</TableCell>
                        <TableCell className="font-semibold">
                          ${(payout.amount / 100).toFixed(2)}
                        </TableCell>
                        <TableCell>{payout.payment_method}</TableCell>
                        <TableCell>{getStatusBadge(payout.status)}</TableCell>
                        <TableCell>
                          {new Date(payout.requested_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {payout.status === "pending" && (
                            <div className="flex gap-2">
                              <Select onValueChange={(value) => updatePayoutStatus(payout.id, value)}>
                                <SelectTrigger className="w-32">
                                  <SelectValue placeholder="Update" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="approved">Approve</SelectItem>
                                  <SelectItem value="paid">Mark Paid</SelectItem>
                                  <SelectItem value="rejected">Reject</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminAffiliates;
